import { Header } from "@general-backoffice/core";
import { type CurrencyStatsItem, type PerformancePoint, USER_POPULATE_FULL } from "@investhinks/api-client";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { Col, Container, Row } from "reactstrap";

import { botConfigsEndpoints } from "../../../../api";
import ExchangeAccountCard from "../../../../components/exchangeAccounts/ExchangeAccountCard";
import useExchangeAccounts from "../../../../components/exchangeAccounts/hooks/useExchangeAccounts";
import GeneralPerformanceGraphCard from "../../../../components/stats/GeneralPerformanceGraphCard";
import StateSummary from "../../../../components/stats/StateSummary";
import useUser from "../../../../components/users/hooks/useUser";
import useFetch from "../../../../hooks/useFetch";

interface CurrencyBreakdown {
  currency: string;
  balance: number;
}

const Referral: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  // TODO: Fix type - getUserGeneralStats expects number but useParams returns string
  const userIdNum = userId ? parseInt(userId, 10) : undefined;

  const { user } = useUser(userId, { params: { populate: USER_POPULATE_FULL } });
  const { name } = user || {};

  const { exchangeAccounts = [] } = useExchangeAccounts({
    filters: { user: { id: userId } },
    depsToReset: [userId],
    sort: { status: "up", "bot_configs.id": "down", "bot_configs.disabled": "down" },
    pagination: { pageSize: 100 },
  });

  const { state: generalStatsResponse, isLoading: isLoadingAccumulatedProfits } = useFetch(
    () =>
      userIdNum ? botConfigsEndpoints.getUserGeneralStats(userIdNum).then(({ data }) => data) : Promise.resolve(null),
    { deps: [userIdNum] }
  );
  const perCurrency: Record<string, CurrencyStatsItem> = generalStatsResponse?.perCurrency ?? {};
  const totals = generalStatsResponse?.totals;

  // Extract accumulated profits history and build currency breakdown for tooltip
  const { accumulatedProfitsPerCurrency, currencyBreakdown } = useMemo(() => {
    const profitsPerCurrency: Record<string, PerformancePoint[]> = {};
    const breakdown: CurrencyBreakdown[] = [];

    for (const [currency, stats] of Object.entries(perCurrency)) {
      if (stats.history) profitsPerCurrency[currency] = stats.history;
      if (stats.activeBalance) {
        breakdown.push({ currency, balance: stats.activeBalance });
      }
    }

    return { accumulatedProfitsPerCurrency: profitsPerCurrency, currencyBreakdown: breakdown };
  }, [perCurrency]);

  // Use pre-calculated totals from API (with currency conversion)
  const activeBots = {
    mainCurrency: totals?.mainCurrency,
    totalBalance: totals?.balance,
    totalDeposits: totals?.deposits,
    totalWithdrawals: totals?.withdrawals,
    count: totals?.activeBots,
    openTrades: totals?.openTrades,
    hasMultipleCurrencies: totals?.hasMultipleCurrencies,
    currencyBreakdown,
  };

  return (
    <React.Fragment>
      <Header.BreadCrumb title={name} color="secondary" isDark={false} />
      <Container className="mt--6" fluid>
        <Row>
          <Col xs={{ size: 12 }} xl={{ size: 4, order: 1 }}>
            <div className="position-sticky" style={{ top: 30, paddingBottom: 0.1 }}>
              <StateSummary
                balance={activeBots?.totalBalance}
                numBots={activeBots?.count}
                numTrades={activeBots?.openTrades}
                withdrawal={activeBots?.totalWithdrawals}
                deposit={activeBots?.totalDeposits}
                currency={activeBots?.mainCurrency}
                hasMultipleCurrencies={activeBots?.hasMultipleCurrencies}
                currencyBreakdown={activeBots?.currencyBreakdown}
              />
            </div>
          </Col>
          <Col xs={{ size: 12 }} xl={{ size: 8, order: 0 }}>
            <GeneralPerformanceGraphCard
              data={accumulatedProfitsPerCurrency}
              mainCurrency={activeBots?.mainCurrency}
              isLoading={isLoadingAccumulatedProfits}
            />
          </Col>
        </Row>
        <Row>
          {(exchangeAccounts || []).map((ea) => {
            const id = ea.id;
            return (
              <Col key={id} xs={12} md={6} lg={4}>
                <ExchangeAccountCard id={id} onClick={() => navigate(`/users/${userId}/exchange-account/${id}`)} />
              </Col>
            );
          })}
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default Referral;
